import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import DishesAPI from '@/features/menu-management/Dishes/api/DishesAPI'
import { QUERY as DISHES_QUERY } from '@/features/menu-management/Dishes/api/DishesAPI.model'
import SidebarAccordionAPI from '@/features/menu-management/SidebarAccordion/api/SidebarAccordionAPI'
import { QUERY as CATEGORIES_QUERY } from '@/features/menu-management/SidebarAccordion/api/SidebarAccordionAPI.model'
import { restaurantCurrency } from '@/shared/utils/price'
import { MenuSnapshot } from '../render/menu'
import { menuSnapshotFrom } from './menuSnapshot'

/**
 * The venue's menu, read only, as the page draws it.
 *
 * The same lists menu management keeps, under the same query keys, so the
 * preview shows the dish somebody has just saved without a second fetch. It
 * asks and shapes; it changes nothing.
 *
 * What the prerender will do on the server against the public menu endpoints
 * (`/public/restaurants/{id}/dishes` and `/categories`), done here through
 * the RMS for the preview.
 */
export function useRestaurantMenu(): { menu: MenuSnapshot | null; isLoading: boolean } {
  const dishes = useQuery({
    queryKey: [DISHES_QUERY.DISHES],
    queryFn: DishesAPI.getDishes
  })
  const categories = useQuery({
    queryKey: [CATEGORIES_QUERY.DISH_CATEGORIES],
    queryFn: SidebarAccordionAPI.getDishCategories
  })

  const menu = useMemo<MenuSnapshot | null>(
    () =>
      dishes.data && categories.data
        ? menuSnapshotFrom(dishes.data, categories.data, restaurantCurrency())
        : null,
    [dishes.data, categories.data]
  )

  return { menu, isLoading: dishes.isLoading || categories.isLoading }
}

export default useRestaurantMenu
